import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { useAuth } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { SubscriptionProvider } from "@/contexts/SubscriptionContext";
import { TeamMessageProvider } from "@/contexts/TeamMessageContext";
import { ClaimNotificationProvider } from "@/contexts/ClaimNotificationContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import ParentProtectedRoute from "@/components/ParentProtectedRoute";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import HomePageNew from "@/pages/HomePageNew";
import ChildrenPage from "@/pages/ChildrenPage";
import DailySignInPage from "@/pages/DailySignInPage";
import UserManagementPage from "@/pages/UserManagementPage";
import StaffRecordsPage from "@/pages/StaffRecordsPage";
import SecuritySettingsPage from "@/pages/SecuritySettingsPage";
import EmergencyContactReportPage from "@/pages/EmergencyContactReportPage";
import BulkUploadPage from "@/pages/BulkUploadPage";
import RecordsBulkUploadPage from "@/pages/RecordsBulkUploadPage";
import PricingPage from "@/pages/PricingPage";
import AboutAppPage from "@/pages/AboutAppPage";
import AttendanceReportPage from "@/pages/AttendanceReportPage";
import AdminCustomersPage from "@/pages/AdminCustomersPage";
import StaffReportsPage from "@/pages/StaffReportsPage";
import ProfilePage from "@/pages/ProfilePage";
import DataBackupPage from "@/pages/DataBackupPage";
import SecureBackupPage from "@/pages/SecureBackupPage";
import CACFPPage from "@/pages/CACFPPage";
import CACFPFormsPage from "@/pages/CACFPFormsPage";
import CustomerFeedbackAdmin from "@/pages/CustomerFeedbackAdmin";
import StaffUploadPage from "@/pages/StaffUploadPage";
import ParentUploadPage from "@/pages/ParentUploadPage";
import FieldTripTagsPage from "@/pages/FieldTripTagsPage";
import ChildServiceReportPage from "@/pages/ChildServiceReportPage";
import ChildServiceReportSummaryPage from "@/pages/ChildServiceReportSummaryPage";
import ComplianceDashboardPage from "@/pages/ComplianceDashboardPage";
import ChildRecordsPage from "@/pages/ChildRecordsPage";
import TourRatioPlannerPage from "@/pages/TourRatioPlannerPage";
import InventoryShoppingListPage from "@/pages/InventoryShoppingListPage";
import BillingPaymentsPage from "@/pages/BillingPaymentsPage";
import PickupSafetyPage from "@/pages/PickupSafetyPage";
import AttendanceTrendsPage from "@/pages/AttendanceTrendsPage";
import PayrollPage from "@/pages/PayrollPage";
import DailyMenuPage from "@/pages/DailyMenuPage";
import LessonPlanningPage from "@/pages/LessonPlanningPage";
import StaffUploadCenterPage from "@/pages/StaffUploadCenterPage";
import StaffClearanceHubPage from "@/pages/StaffClearanceHubPage";
import ProfessionalDevelopmentHubPage from "@/pages/ProfessionalDevelopmentHubPage";
import PolicyAcknowledgementsPage from "@/pages/PolicyAcknowledgementsPage";
import AcknowledgePage from "@/pages/AcknowledgePage";
import OwnerToolsPage from "@/pages/OwnerToolsPage";
import AdminPlatformRevenuePage from "@/pages/AdminPlatformRevenuePage";
import EmailActivityPage from "@/pages/EmailActivityPage";
import ParentCommunicationSettingsPage from "@/pages/ParentCommunicationSettingsPage";
import FamilyCommunicationsHubPage from "@/pages/FamilyCommunicationsHubPage";
import ParentCommunicationCenterPage from "@/pages/ParentCommunicationCenterPage";
import FormBuilderPage from "@/pages/FormBuilderPage";
import FormBuilderHubPage from "@/pages/FormBuilderHubPage";
import ParentFormsPage from "@/pages/ParentFormsPage";
import FeaturesGuidePage from "@/pages/FeaturesGuidePage";
import KioskPage from "@/pages/KioskPage";
import PrintKeychainCardsPage from "@/pages/PrintKeychainCardsPage";
import QRSignInPage from "@/pages/QRSignInPage";
import ParentSMSCommunicationsPage from "@/pages/ParentSMSCommunicationsPage";
import ParentLoginPage from "@/pages/ParentLoginPage";
import ParentInviteAcceptPage from "@/pages/ParentInviteAcceptPage";
import ParentPortalPage from "@/pages/ParentPortalPage";
import ParentMessagesPage from "@/pages/ParentMessagesPage";
import ParentActivityPage from "@/pages/ParentActivityPage";
import ParentDocumentsPage from "@/pages/ParentDocumentsPage";
import ParentBillingPage from "@/pages/ParentBillingPage";
import EnrollmentPortalPage from "@/pages/EnrollmentPortalPage";
import PendingEnrollmentPortalPage from "@/pages/PendingEnrollmentPortalPage";

const HomeRouter = () => {
  const { user } = useAuth();
  if (user?.role?.toLowerCase() === "parent") {
    return <Navigate to="/parent" replace />;
  }
  return <HomePageNew />;
};

function App() {
  return (
    <div className="App">
      <LanguageProvider>
        <AuthProvider>
          <SubscriptionProvider>
            <TeamMessageProvider>
              <ClaimNotificationProvider>
              <BrowserRouter>
                <Routes>
                {/* Public route for parent document upload (shot records, health records, and all forms) */}
                <Route path="/parent-upload/:token/:docType" element={<ParentUploadPage />} />
            <Route path="/staff-upload/:token/:clearanceType" element={<StaffUploadPage />} />
                <Route path="/enrollment/:token" element={<EnrollmentPortalPage />} />
                <Route path="/pending-enrollment/:token" element={<PendingEnrollmentPortalPage />} />
                
                <Route path="/login" element={<LoginPage />} />
                <Route path="/parent/login" element={<ParentLoginPage />} />
                <Route path="/parent/invite/:token" element={<ParentInviteAcceptPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/start" element={<RegisterPage />} />
                <Route path="/start-trial" element={<RegisterPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
                <Route path="/verify-email" element={<VerifyEmailPage />} />
                <Route path="/secure-backup/:token" element={<SecureBackupPage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/about" element={<AboutAppPage />} />
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <HomeRouter />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/parent"
                  element={
                    <ParentProtectedRoute>
                      <ParentPortalPage />
                    </ParentProtectedRoute>
                  }
                />
                <Route
                  path="/parent/messages"
                  element={
                    <ParentProtectedRoute>
                      <ParentMessagesPage />
                    </ParentProtectedRoute>
                  }
                />
                <Route
                  path="/parent/activity"
                  element={
                    <ParentProtectedRoute>
                      <ParentActivityPage />
                    </ParentProtectedRoute>
                  }
                />
                <Route path="/parent/documents" element={<ParentProtectedRoute><ParentDocumentsPage /></ParentProtectedRoute>} />
                <Route path="/parent/billing" element={<ParentProtectedRoute><ParentBillingPage /></ParentProtectedRoute>} />
                <Route
                  path="/children"
                  element={
                    <ProtectedRoute>
                      <ChildrenPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/child-records"
                  element={
                    <ProtectedRoute allowedRoles={['owner', 'co-owner']}>
                      <ChildRecordsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/tour-ratio-planner"
                  element={
                    <ProtectedRoute>
                      <TourRatioPlannerPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/inventory-shopping-list"
                  element={
                    <ProtectedRoute>
                      <InventoryShoppingListPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/signin/:date"
                  element={
                    <ProtectedRoute>
                      <DailySignInPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/users"
                  element={
                    <ProtectedRoute>
                      <UserManagementPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/staff-records"
                  element={
                    <ProtectedRoute>
                      <StaffRecordsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/staff/clearance-hub"
                  element={
                    <ProtectedRoute>
                      <StaffClearanceHubPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/staff/professional-development"
                  element={<ProtectedRoute><ProfessionalDevelopmentHubPage /></ProtectedRoute>}
                />
                <Route path="/settings/policy-acknowledgements" element={<ProtectedRoute><PolicyAcknowledgementsPage /></ProtectedRoute>} />
                <Route path="/acknowledge/:token" element={<AcknowledgePage />} />
                <Route
                  path="/security"
                  element={
                    <ProtectedRoute>
                      <SecuritySettingsPage />
                    </ProtectedRoute>
                  }
                />
                {/* Family Communications Hub */}
                <Route
                  path="/family-communications"
                  element={
                    <ProtectedRoute allowedRoles={['owner', 'co-owner', 'business-manager', 'staff']}>
                      <FamilyCommunicationsHubPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/parent-communication-center"
                  element={
                    <ProtectedRoute allowedRoles={['owner', 'co-owner', 'business-manager', 'staff']}>
                      <ParentCommunicationCenterPage />
                    </ProtectedRoute>
                  }
                />
                {/* Legacy — preserve old bookmarks by redirecting to the new hub */}
                <Route path="/forms" element={<Navigate to="/form-builder?mode=digital" replace />} />
                <Route
                  path="/form-builder"
                  element={
                    <ProtectedRoute allowedRoles={['owner', 'co-owner']}>
                      <FormBuilderHubPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/parent/forms" element={<ParentFormsPage />} />
                {/* QR Sign-In — public, no auth needed */}
                <Route path="/qr-signin/:token" element={<QRSignInPage />} />
                <Route path="/features-guide" element={
                  <ProtectedRoute allowedRoles={['owner','co-owner','business-manager','staff']}>
                    <FeaturesGuidePage />
                  </ProtectedRoute>
                } />
                {/* Kiosk Scanner */}
                <Route path="/kiosk" element={<ProtectedRoute allowedRoles={['owner','co-owner','business-manager','staff']}><KioskPage /></ProtectedRoute>} />
                {/* Print Keychain Cards */}
                <Route path="/print-keychain-cards" element={<ProtectedRoute allowedRoles={['owner','co-owner','business-manager']}><PrintKeychainCardsPage /></ProtectedRoute>} />
                {/* Legacy redirect — old portal URL still works, lands on portal tab */}
                <Route
                  path="/settings/parent-communication"
                  element={
                    <ProtectedRoute allowedRoles={['owner']}>
                      <ParentCommunicationSettingsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/parent-communication"
                  element={
                    <ProtectedRoute allowedRoles={['owner', 'co-owner']}>
                      <ParentSMSCommunicationsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/owner-tools"
                  element={
                    <ProtectedRoute allowedRoles={['owner']} primaryOwnerOnly>
                      <OwnerToolsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/email-activity"
                  element={
                    <ProtectedRoute allowedRoles={['owner', 'co-owner', 'business-manager', 'staff']} permissionKey="email_activity">
                      <EmailActivityPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/platform-revenue"
                  element={
                    <ProtectedRoute allowedRoles={['owner']} primaryOwnerOnly>
                      <AdminPlatformRevenuePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/emergency-contacts"
                  element={
                    <ProtectedRoute>
                      <EmergencyContactReportPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/emergency-contacts/:childId"
                  element={
                    <ProtectedRoute>
                      <EmergencyContactReportPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/children/bulk-upload"
                  element={
                    <ProtectedRoute allowedRoles={['owner', 'co-owner']}>
                      <BulkUploadPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/children/records-bulk-upload"
                  element={
                    <ProtectedRoute allowedRoles={['owner', 'co-owner']}>
                      <RecordsBulkUploadPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/attendance-report"
                  element={
                    <ProtectedRoute allowedRoles={['owner', 'co-owner']}>
                      <AttendanceReportPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/cacfp" element={<ProtectedRoute><CACFPPage /></ProtectedRoute>} />
                <Route path="/cacfp/forms" element={<ProtectedRoute><CACFPFormsPage /></ProtectedRoute>} />
                <Route
                  path="/daily-menu"
                  element={
                    <ProtectedRoute>
                      <DailyMenuPage key="lunch" menuType="lunch" />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/daily-menu/lunch"
                  element={
                    <ProtectedRoute>
                      <DailyMenuPage key="lunch" menuType="lunch" />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/daily-menu/breakfast-snack"
                  element={
                    <ProtectedRoute>
                      <DailyMenuPage key="breakfast_snack" menuType="breakfast_snack" />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/lesson-planning"
                  element={
                    <ProtectedRoute>
                      <LessonPlanningPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/staff-uploads"
                  element={
                    <ProtectedRoute>
                      <StaffUploadCenterPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/field-trip-tags"
                  element={
                    <ProtectedRoute>
                      <FieldTripTagsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/child-service-report"
                  element={
                    <ProtectedRoute>
                      <ChildServiceReportPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/child-service-report-summary"
                  element={
                    <ProtectedRoute allowedRoles={['owner', 'co-owner']}>
                      <ChildServiceReportSummaryPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/compliance-dashboard"
                  element={
                    <ProtectedRoute allowedRoles={['owner', 'co-owner']}>
                      <ComplianceDashboardPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/attendance-trends"
                  element={
                    <ProtectedRoute allowedRoles={['owner', 'co-owner']}>
                      <AttendanceTrendsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/customers"
                  element={
                    <ProtectedRoute allowedRoles={['owner']} primaryOwnerOnly>
                      <AdminCustomersPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/staff-reports"
                  element={
                    <ProtectedRoute>
                      <StaffReportsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <ProfilePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/backup"
                  element={
                    <ProtectedRoute>
                      <DataBackupPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/customer-feedback"
                  element={
                    <ProtectedRoute allowedRoles={['owner']}>
                      <CustomerFeedbackAdmin />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/billing/payments"
                  element={
                    <ProtectedRoute allowedRoles={['owner', 'co-owner']}>
                      <BillingPaymentsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/billing/auto-invoicing"
                  element={
                    <ProtectedRoute allowedRoles={['owner', 'co-owner']}>
                      <BillingPaymentsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/pickup-safety"
                  element={
                    <ProtectedRoute allowedRoles={['owner', 'co-owner']}>
                      <PickupSafetyPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/billing/payroll"
                  element={
                    <ProtectedRoute allowedRoles={['owner', 'co-owner']}>
                      <PayrollPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              </BrowserRouter>
              </ClaimNotificationProvider>
            </TeamMessageProvider>
          </SubscriptionProvider>
        </AuthProvider>
      </LanguageProvider>
    </div>
  );
}

export default App;
